<template>
  <div>
    <div class="grid grid-cols-1 gap-4 py-2 md:py-4  ">
      <!-- Üst Satır: Görsel ve Ürün Bilgileri -->
      <div class="grid grid-cols-[auto,1fr] gap-4">
        <!-- Ürün Görseli -->
        <div class="relative ">
          <NuxtLink :to="item.url">
            <NuxtImg v-if="item.image" :src="'cl' + item.image?.path" :class="{ 'opacity-50': !item.is_in_stock }"
              class="border rounded-md" width="96" height="96" fit="inside" background="white" />
            <img v-else src="/img-placeholder.jpg" class="w-[96px] h-[96px] object-cover rounded-lg" />
          </NuxtLink>
        </div>

        <!-- Ürün Detayları -->
        <div class="min-w-0">
          <div class="flex justify-between">
            <NuxtLink :to="item.url" class="text-sm font-medium text-sky-500 line-clamp-2">
              <h3 class="text-sm font-medium text-sky-500 line-clamp-2">
                {{ item.product?.name }}
              </h3>
            </NuxtLink>

            <UButton icon="i-heroicons-x-mark" variant="ghost" color="gray" size="sm" class="flex-shrink-0 ml-2"
              :loading="isRemoveLoading" :disabled="isRemoveLoading" @click="removeItem" />
          </div>

          <!-- Varyasyon Bilgileri -->
          <div v-if="item.variation?.terms" class="mt-1">
            <div v-for="term in item.variation.terms" :key="term.id" class="text-xs text-slate-500">
              {{
                term.product_attribute_term.attribute_term.attribute.attribute_name
              }}:
              {{ term.product_attribute_term.attribute_term.term.name }}
            </div>
          </div>

          <!-- Stok Durumu Uyarıları -->
          <template v-if="!item.is_in_stock">
            <div class="mt-2">
              <div class="text-red-600 text-sm font-medium">
                Bu ürün stokta kalmamıştır
              </div>
            </div>
          </template>

          <template v-else>
            <div v-if="!item.is_in_stock_enough" class="mt-2 space-y-2">
              <div class="text-orange-600 text-sm">
                Satıcının ürün stoğu sepetinizdeki miktardan daha az. Mevcut stok:
                <b>{{ item.winner_data?.stock_qty }}</b>
              </div>
              <div class="flex flex-wrap gap-2">
                <UButton size="xs" color="orange" variant="soft" @click="updateCartWithAvailableStock">
                  Mevcut adet ile sepeti güncelle
                </UButton>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Alt Satır: Adet, Fiyat ve Uyarılar -->
      <div class="pl-0 ">
        <template v-if="item.is_in_stock">
          <!-- Fiyat ve Miktar -->
          <div class="flex items-center justify-between">
            <div class="flex items-center border rounded-lg overflow-hidden">
              <UButton icon="i-heroicons-minus-small" variant="ghost" color="gray" size="sm"
                :ui="{ padding: 'px-2 py-1' }" :disabled="quantity <= 1 || isDecreaseLoading"
                :loading="isDecreaseLoading" @click="decrease" />
              <span class="py-1 text-sm font-medium text-slate-900 min-w-[30px] text-center">
                {{ quantity }}
              </span>
              <UButton icon="i-heroicons-plus-small" variant="ghost" color="gray" size="sm"
                :ui="{ padding: 'px-2 py-1' }" :disabled="isIncreaseDisabled || isIncreaseLoading"
                :loading="isIncreaseLoading" @click="increase" />
            </div>
            <div>
              <div class="flex items-center space-x-2 text-sm">
                <template v-if="
                  item.winner_data?.sale_price &&
                  item.winner_data?.sale_price !== item.winner_data?.price
                ">
                  <span class="line-through text-slate-400">{{
                    formatPrice(item.winner_data.price * item.quantity)
                    }}</span>
                  <span class="font-medium text-orange-500">{{
                    formatPrice(item.winner_data.sale_price * item.quantity)
                    }}</span>
                </template>
                <template v-else>
                  <span class="font-medium text-orange-500">{{
                    formatPrice(getItemPrice)
                    }}</span>
                </template>
              </div>
            </div>
          </div>

          <!-- Fiyat Değişikliği Uyarısı -->
          <div v-if="hasPriceChanged" class="mt-2 flex justify-end">
            <div class="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md inline-flex items-center">
              <UIcon name="i-heroicons-exclamation-triangle" class="w-3.5 h-3.5 text-amber-500 mr-1.5 flex-shrink-0" />
              Fiyat değişikliği
              <UButton color="amber" variant="ghost" class="p-0 ml-1.5" :ui="{ rounded: 'rounded-full' }"
                @click="showPriceInfoModal = true">
                <UIcon name="i-heroicons-information-circle" class="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              </UButton>
            </div>
          </div>

          <!-- Satıcı Değişikliği -->
          <div v-if="hasVendorChanged" class="mt-1 flex justify-end">
            <div class="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md inline-flex items-center">
              <UIcon name="i-heroicons-building-storefront" class="w-3.5 h-3.5 text-amber-500 mr-1.5 flex-shrink-0" />
              Satıcı değişikliği
              <UButton color="amber" variant="ghost" class="p-0 ml-1.5" :ui="{ rounded: 'rounded-full' }"
                @click="showVendorInfoModal = true">
                <UIcon name="i-heroicons-information-circle" class="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              </UButton>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Fiyat Değişikliği Bilgi Modalı -->
    <UModal v-model="showPriceInfoModal" :ui="{ width: 'sm:max-w-md' }">
      <UCard>
        <template #header>
          <div class="flex items-center">
            <UIcon name="i-heroicons-exclamation-triangle" class="w-5 h-5 text-amber-500 mr-2" />
            <h3 class="text-base font-medium">Fiyat Değişikliği Bilgisi</h3>
          </div>
        </template>
        <div class="text-sm text-slate-600 space-y-3">
          <p>
            Ürünün fiyatı sepete eklediğiniz zamandan bu yana değişmiştir. Güncel fiyat üzerinden işlem yapılacaktır.
          </p>
          <div class="bg-amber-50 p-3 rounded-md">
            <div class="flex items-center justify-between">
              <span class="font-medium">Eski Fiyat:</span>
              <span class="line-through">{{ formatPrice(item.original_price) }}</span>
            </div>
            <div class="flex items-center justify-between mt-1">
              <span class="font-medium">Yeni Fiyat:</span>
              <span class="font-medium text-orange-500">{{ formatPrice(item.winner_data.sale_price) }}</span>
            </div>
          </div>
        </div>
        <template #footer>
          <div class="flex justify-end">
            <UButton color="gray" variant="soft" @click="showPriceInfoModal = false">
              Kapat
            </UButton>
          </div>
        </template>
      </UCard>
    </UModal>

    <!-- Satıcı Değişikliği Bilgi Modalı -->
    <UModal v-model="showVendorInfoModal" :ui="{ width: 'sm:max-w-md' }">
      <UCard>
        <template #header>
          <div class="flex items-center">
            <UIcon name="i-heroicons-building-storefront" class="w-5 h-5 text-amber-500 mr-2" />
            <h3 class="text-base font-medium">Satıcı Değişikliği Bilgisi</h3>
          </div>
        </template>
        <p class="text-sm text-slate-600">
          Ürünün satıcısı sepete eklediğiniz zamandan bu yana değişmiştir. Güncel satıcı üzerinden işlem yapılacaktır.
        </p>
        <template #footer>
          <div class="flex justify-end">
            <UButton color="gray" variant="soft" @click="showVendorInfoModal = false">
              Kapat
            </UButton>
          </div>
        </template>
      </UCard>
    </UModal>
  </div>
</template>

<script setup>
const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const cartStore = useCartStore()
const quantity = ref(props.item.quantity)
const isDecreaseLoading = ref(false)
const isIncreaseLoading = ref(false)
const isRemoveLoading = ref(false)
const showPriceInfoModal = ref(false)
const showVendorInfoModal = ref(false)

// Fiyat değişikliği kontrolü
const hasPriceChanged = computed(() => {
  return props.item.original_price !== props.item.winner_data?.sale_price
})

// Satıcı değişikliği kontrolü
const hasVendorChanged = computed(() => {
  return props.item.original_vendor_id !== props.item.winner_data?.vendor?.id
})

// Maksimum stok miktarını hesapla
const maxQuantity = computed(() => {
  return props.item.winner_data?.stock_qty || 0
})

// Artırma butonunun disable durumunu kontrol et
const isIncreaseDisabled = computed(() => {
  // Stokta yoksa veya stok yetersizse
  if (!props.item.is_in_stock || !props.item.is_in_stock_enough) {
    return true
  }
  // Mevcut miktar maksimum stok miktarına eşit veya fazlaysa
  return quantity.value >= maxQuantity.value
})

const getItemPrice = computed(() => {
  const price =
    props.item.winner_data?.sale_price ?? props.item.winner_data?.price
  return price * props.item.quantity
})

// Miktar azaltma
const decrease = async () => {
  if (quantity.value > 1) {
    isDecreaseLoading.value = true
    try {
      const newQuantity = quantity.value - 1
      await cartStore.updateCartItemQuantity(props.item, newQuantity)
      quantity.value = newQuantity
    } finally {
      isDecreaseLoading.value = false
    }
  }
}

// Miktar artırma
const increase = async () => {
  if (!isIncreaseDisabled.value) {
    isIncreaseLoading.value = true
    try {
      const newQuantity = quantity.value + 1
      await cartStore.updateCartItemQuantity(props.item, newQuantity)
      quantity.value = newQuantity
    } finally {
      isIncreaseLoading.value = false
    }
  }
}

const updateQuantity = async (newQuantity) => {
  // Stok kontrolü
  if (newQuantity > maxQuantity.value) {
    newQuantity = maxQuantity.value
  }
  await cartStore.updateCartItemQuantity(props.item, newQuantity)
  quantity.value = newQuantity
}

const updateCartWithAvailableStock = async () => {
  isDecreaseLoading.value = true
  try {
    await updateQuantity(maxQuantity.value)
  } finally {
    isDecreaseLoading.value = false
  }
}

const removeItem = async () => {
  isRemoveLoading.value = true
  try {
    await cartStore.removeFromCart(props.item.id)
  } finally {
    isRemoveLoading.value = false
  }
}
</script>

<style scoped>
/* Input'taki ok butonlarını gizle */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
